// Hash Navigation Utility for GSAP ScrollSmoother Integration
// Based on GSAP forum solution for smooth scrolling with hash navigation

// Register ScrollToPlugin if available
if (window.gsap && window.ScrollToPlugin) {
  window.gsap.registerPlugin(window.ScrollToPlugin);
}

// Detect mobile devices
const isMobileDevice = () => {
  return window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Global state to prevent race conditions
let navigationState = {
  isNavigating: false,
  pendingTimeouts: new Set(),
  currentHash: '',
  lastNavigationTime: 0,
  isInitialized: false,
  navigationStartTime: 0
};

// Override any scroll spy or hash updating during navigation
let originalHistoryMethods = null;

function preventHashUpdatesFromScrollSpy() {
  if (originalHistoryMethods) return;

  originalHistoryMethods = {
    pushState: window.history.pushState,
    replaceState: window.history.replaceState
  };

  window.history.pushState = function(state, title, url) {
    if (navigationState.isNavigating && url && url.includes('#')) {
      const now = Date.now();
      if (now - navigationState.navigationStartTime < 3000) {
        return;
      }
    }
    return originalHistoryMethods.pushState.call(this, state, title, url);
  };

  window.history.replaceState = function(state, title, url) {
    if (navigationState.isNavigating && url && url.includes('#')) {
      const now = Date.now();
      const targetHash = navigationState.currentHash;
      if (now - navigationState.navigationStartTime < 3000 && !url.includes(targetHash)) {
        return;
      }
    }
    return originalHistoryMethods.replaceState.call(this, state, title, url);
  };
}

function restoreHistoryMethods() {
  if (originalHistoryMethods) {
    window.history.pushState = originalHistoryMethods.pushState;
    window.history.replaceState = originalHistoryMethods.replaceState;
    originalHistoryMethods = null;
  }
}

// Prevent default browser hash navigation
function preventDefaultHashNavigation() {
  // Prevent automatic scroll on hash navigation
  let lastScrollTime = 0;

  // Override the browser's automatic hash scrolling
  const originalScrollTo = window.scrollTo;
  const originalScrollBy = window.scrollBy;

    window.scrollTo = function(x, y) {
    const now = Date.now();

    if (navigationState.isNavigating) {
      return originalScrollTo.apply(this, arguments);
    }

    if (now - lastScrollTime < 100 && arguments.length === 2 && typeof x === 'number' && typeof y === 'number') {
      return;
    }
    return originalScrollTo.apply(this, arguments);
  };

    window.scrollBy = function(x, y) {
    const now = Date.now();

    if (navigationState.isNavigating) {
      return originalScrollBy.apply(this, arguments);
    }

    if (now - lastScrollTime < 100) {
      return;
    }
    return originalScrollBy.apply(this, arguments);
  };

  // Track when hash changes occur
  window.addEventListener('hashchange', () => {
    lastScrollTime = Date.now();
  }, true);

  // Prevent default hash navigation on page load
  // Note: We don't force scroll to top here anymore - SmoothScroll component handles this
  window.addEventListener('DOMContentLoaded', () => {
    if (window.location.hash && !window.location.hash.includes('/')) {
      // Just prevent the default browser scroll, but don't force to top
      window.history.scrollRestoration = 'manual';
    }
  });

  // Also prevent on window load
  window.addEventListener('load', () => {
    if (window.location.hash && !window.location.hash.includes('/')) {
      window.history.scrollRestoration = 'manual';
    }
  });
}

// Detect if a link's href goes to the current page
function getSamePageAnchor(link) {
  if (
    link.protocol !== window.location.protocol ||
    link.host !== window.location.host ||
    link.pathname !== window.location.pathname ||
    link.search !== window.location.search
  ) {
    return false;
  }

  return link.hash;
}

// Clear all pending navigation timeouts
function clearPendingTimeouts() {
  navigationState.pendingTimeouts.forEach(timeoutId => {
    clearTimeout(timeoutId);
  });
  navigationState.pendingTimeouts.clear();
}

// Scroll to a given hash, preventing the event given if there is one
function scrollToHash(hash, e, force = false) {
  // Skip hash navigation if hash contains paths (Flutter routes)
  if (hash && hash.includes('/')) {
    return;
  }

  let elem = false;
  if (hash) {
    try {
      elem = document.querySelector(hash);
    } catch (error) {
      // Invalid CSS selector, skip it
      return;
    }
  }

  if (!elem || !hash) return;

  // Always prevent default browser behavior
  if (e) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  // Prevent race conditions by clearing any pending navigations
  clearPendingTimeouts();

  // Kill any existing GSAP scrolls
  if (window.gsap) {
    window.gsap.killTweensOf(window);
  }

  // Check if we're already navigating to this hash (unless forced)
  if (!force && navigationState.isNavigating && navigationState.currentHash === hash) {
    return;
  }



  // Update navigation state
  navigationState.isNavigating = true;
  navigationState.currentHash = hash;
  navigationState.lastNavigationTime = Date.now();
  navigationState.navigationStartTime = Date.now();

  // Prevent scroll spy from interfering
  preventHashUpdatesFromScrollSpy();

  // Update URL hash immediately to prevent conflicts
  if (hash !== window.location.hash) {
    window.history.replaceState(null, null, hash);
  }

    // Wait for layout to stabilize before scrolling (important for dynamic content)
  const stabilizeLayout = () => {
    return new Promise(resolve => {
      // Force layout refresh to get accurate measurements
      if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
      }

      // Force layout recalculation
      // eslint-disable-next-line no-unused-vars
      const _ = document.body.offsetHeight;

      // Wait for any pending animations or dynamic content
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  // Only skip scroll if we're exactly at the target element AND it was the last navigation target
  const rect = elem.getBoundingClientRect();
  const isAlreadyAtTop = Math.abs(rect.top) < 5; // tighter tolerance
  const wasLastTarget = navigationState.currentHash === hash;

  if (isAlreadyAtTop && !force && wasLastTarget) {
    navigationState.isNavigating = false;
    return;
  }

      // Use optimized mobile scrolling
  if (isMobileDevice()) {
    const targetY = Math.max(0, elem.offsetTop - 20);

    try {
      const originalScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';

      const scrollBefore = window.scrollY;

      window.scrollTo({
        top: targetY,
        behavior: 'smooth'
      });

      setTimeout(() => {
        const scrollAfter = window.scrollY;
        if (Math.abs(scrollAfter - scrollBefore) < 50) {
          elem.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 200);

      setTimeout(() => {
        document.documentElement.style.scrollBehavior = originalScrollBehavior;
      }, 100);

    } catch (error) {
      elem.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }

    const resetTimeout = setTimeout(() => {
      navigationState.isNavigating = false;
      setTimeout(() => {
        restoreHistoryMethods();
      }, 50);
    }, 600);

    navigationState.pendingTimeouts.add(resetTimeout);
    return;
  }

  // Use GSAP ScrollSmoother if available, otherwise fall back to GSAP ScrollTo
  if (window.ScrollSmoother && window.ScrollSmoother.get()) {

    // Wait for layout stabilization before calculating positions
    stabilizeLayout().then(() => {
      const smoother = window.ScrollSmoother.get();
      const alternativeY = elem.offsetTop;
      const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;

      // If the element is near the bottom, scroll to show the element properly
              if (alternativeY > maxScrollY) {
        const elementHeight = elem.offsetHeight;
        const distanceBeyondMax = alternativeY - maxScrollY;

        if (distanceBeyondMax < elementHeight * 0.3) {
          smoother.scrollTo(maxScrollY, true, "power2.inOut");
        } else {
          const betterTarget = alternativeY - (window.innerHeight / 2) + (elementHeight / 2);
          const finalTarget = Math.min(Math.max(betterTarget, 0), maxScrollY);
          smoother.scrollTo(finalTarget, true, "power2.inOut");
        }
      } else {
        smoother.scrollTo(alternativeY, true, "power2.inOut");
      }

      const resetTimeout = setTimeout(() => {
        navigationState.isNavigating = false;
        navigationState.pendingTimeouts.delete(resetTimeout);

        setTimeout(() => {
          restoreHistoryMethods();
        }, 1000);

        setTimeout(() => {
          try {
            if (navigationState.currentHash === hash) {
              window.ScrollTrigger?.refresh();
            }
          } catch (_) {}
        }, 500);
      }, 800);

      navigationState.pendingTimeouts.add(resetTimeout);
    });
  } else if (window.gsap && window.gsap.to && window.ScrollToPlugin) {

    window.gsap.to(window, {
      duration: 1.5,
      scrollTo: {
        y: elem,
        offsetY: 0,
        autoKill: false
      },
      ease: "power2.inOut",
      onComplete: () => {
        navigationState.isNavigating = false;
        setTimeout(() => {
          restoreHistoryMethods();
        }, 100);
      }
    });
  } else {
    elem.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });

    const resetTimeout = setTimeout(() => {
      navigationState.isNavigating = false;
      navigationState.pendingTimeouts.delete(resetTimeout);
      setTimeout(() => {
        restoreHistoryMethods();
      }, 1000);
    }, 1500);

    navigationState.pendingTimeouts.add(resetTimeout);
  }
}

// Initialize hash navigation system
export function initHashNavigation() {
  // Prevent multiple initializations
  if (navigationState.isInitialized) {
    return () => {}; // Return empty cleanup function
  }

  navigationState.isInitialized = true;

  // Prevent default browser hash navigation
  preventDefaultHashNavigation();

  // Handle clicks on same-page anchor links
  const handleAnchorClick = (e) => {
    const target = e.target.closest('a[href]');
    if (target) {
      const hash = getSamePageAnchor(target);
      if (hash) {
        e.preventDefault();
        e.stopImmediatePropagation();
        scrollToHash(hash, e);
      }
    }
  };

  // Add event listener to document for event delegation with high priority
  document.addEventListener('click', handleAnchorClick, true); // Use capture phase

  // Handle hash changes from browser navigation (back/forward)
  const handleHashChange = (e) => {
    const currentHash = window.location.hash;

    e.preventDefault();

    if (navigationState.isNavigating && navigationState.currentHash !== currentHash) {
      clearPendingTimeouts();
      navigationState.isNavigating = false;
    }

    const now = Date.now();
    if (navigationState.currentHash === currentHash && now - navigationState.lastNavigationTime < 100) {
      return;
    }

    if (currentHash) {
      clearPendingTimeouts();
      scrollToHash(currentHash, null, true);
    }
  };

  window.addEventListener('hashchange', handleHashChange, true); // Use capture phase

  // Handle initial hash on page load
  const handleInitialHash = () => {
    const currentHash = window.location.hash;

    // IMPORTANT: Only handle hash if there actually is a hash
    // Don't do anything if there's no hash - avoid interfering with scroll position
    if (!currentHash) {
      return;
    }

    // For initial page load with hash, use instant scroll to prevent jump-scroll-jump issue
    // Wait for React components to mount and elements to be available
    let retryCount = 0;
    const maxRetries = 10; // Maximum 10 retries (2 seconds total)

    const checkAndScrollToHash = () => {
      // Skip Flutter routes (they start with #/ and have slashes)
      if (currentHash.includes('/')) {
        return;
      }

      try {
        const elem = document.querySelector(currentHash);
        if (elem) {
          scrollToHash(currentHash, null, false);
        } else if (retryCount < maxRetries) {
          retryCount++;
          const retryTimeout = setTimeout(() => {
            checkAndScrollToHash();
            navigationState.pendingTimeouts.delete(retryTimeout);
          }, 200);
          navigationState.pendingTimeouts.add(retryTimeout);
        }
      } catch (error) {
        // Invalid CSS selector, skip it
      }
    };

    // Start checking after allowing time for React components to mount
    const initialTimeout = setTimeout(() => {
      checkAndScrollToHash();
      navigationState.pendingTimeouts.delete(initialTimeout);
    }, 500); // Increased delay to ensure React components are mounted

    navigationState.pendingTimeouts.add(initialTimeout);
  };

  // Check for hash on initial load and after window load
  handleInitialHash();
  window.addEventListener('load', handleInitialHash);

  // Also try on DOMContentLoaded as backup
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleInitialHash);
  } else {
    // If DOM is already loaded, try again
    setTimeout(() => {
      handleInitialHash();
    }, 100);
  }

  // Return cleanup function
  return () => {
    clearPendingTimeouts();
    navigationState.isNavigating = false;
    navigationState.isInitialized = false;

    restoreHistoryMethods();

    document.removeEventListener('click', handleAnchorClick, true);
    window.removeEventListener('hashchange', handleHashChange, true);
    window.removeEventListener('load', handleInitialHash);
  };
}

// Utility function to scroll to a specific section by ID (uses smooth scrolling)
export function scrollToSection(sectionId) {
  const hash = sectionId.startsWith('#') ? sectionId : `#${sectionId}`;

  clearPendingTimeouts();
  scrollToHash(hash);
}

// Utility function to get all navigable sections
export function getNavigableSections() {
  const sections = document.querySelectorAll('section[id], footer[id]');
  return Array.from(sections).map(section => ({
    id: section.id,
    element: section,
    title: section.getAttribute('data-nav-title') || section.id,
  }));
}

// Utility to check which section is currently in view
export function getCurrentSection() {
  const sections = getNavigableSections();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const windowHeight = window.innerHeight;

  // Find the section that's most visible in the viewport
  let currentSection = null;
  let maxVisibility = 0;

  sections.forEach(section => {
    const rect = section.element.getBoundingClientRect();
    const elementTop = rect.top + scrollTop;
    const elementBottom = elementTop + rect.height;

    // Calculate how much of the element is visible
    const visibleTop = Math.max(elementTop, scrollTop);
    const visibleBottom = Math.min(elementBottom, scrollTop + windowHeight);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibility = visibleHeight / windowHeight;

    if (visibility > maxVisibility) {
      maxVisibility = visibility;
      currentSection = section;
    }
  });

  return currentSection;
}

// Reset navigation state (useful for debugging or cleanup)
export function resetNavigationState() {
  clearPendingTimeouts();
  navigationState.isNavigating = false;
  navigationState.currentHash = '';
  navigationState.lastNavigationTime = 0;
  navigationState.navigationStartTime = 0;
  restoreHistoryMethods();
}

// Default export
const hashNavigationUtils = {
  init: initHashNavigation,
  scrollToSection,
  scrollToHash,
  getNavigableSections,
  getCurrentSection,
  getSamePageAnchor,
  resetNavigationState
};

export default hashNavigationUtils;